import Grid from '@mui/material/Grid';
import classNames from 'classnames';
import { autobind } from 'core-decorators';
import PropTypes from 'prop-types';
import { Component, Fragment } from 'react';
import ReactMarkdown from 'react-markdown';
import { connect } from 'react-redux';
import ShowMoreText from 'react-show-more-text';
import { bindActionCreators } from 'redux';
import NewReferenceItems from '/@/components/map/new-reference-items';
import ScrollableChips from '/@/components/scrollable-chips';
import settings from '/@/core/settings';
import { _ } from '/@/locale';

import { setVersionLabel } from '/@/store/maps/actions';
import { getVersionLabel } from '/@/store/maps/selectors';
import Select from '/@/v3/components/forms/select';
import Title from '/@/v3/components/title';
import styles from './styles.module.css';
@connect(
    (state) => ({ versionLabel: getVersionLabel(state) }),
    (dispatch) => ({ ...bindActionCreators({ setVersionLabel }, dispatch), dispatch })
)
@autobind
export default class NewItemOverlay extends Component {
    static propTypes = {
        overlay: PropTypes.object,
        version: PropTypes.object,
        onOverlayChange: PropTypes.func,
    };

    componentDidUpdate(prevProps) {
        if (prevProps.tab !== this.props.tab) {
            this.setState({ overlayShowed: undefined });
        }
    }

    state = {
        overlayShowed: undefined,
    };

    handleVersionChange(value) {
        const versionSelected = this.props.overlay.versions.find(({ versionNumber }) => versionNumber === value);
        this.props.setVersionLabel(_(versionSelected.versionLabel));

        this.props.onOverlayChange({
            type: this.props.overlay.slug,
            version: value,
        });
    }

    renderVersionSelect(versions) {
        const options = versions.map((v) => ({ label: _(v.versionLabel), value: v.versionNumber }));

        return (
            <Select
                appearance='outline'
                color={settings.app.theme}
                size='md'
                value={this.props.version.versionNumber}
                options={options}
                onChange={this.handleVersionChange}
                placeholder={_('Seleccione una version')}
                className={styles.versionSelect}
                maxHeight='200px'
            />
        );
    }

    string_to_slug(str) {
        str = str.replace(/^\s+|\s+$/g, ''); // trim
        str = str.toLowerCase();

        // remove accents, swap ñ for n, etc
        const from = 'àáäâèéëêìíïîòóöôùúüûñç·/_,:;';
        const to = 'aaaaeeeeiiiioooouuuunc------';
        for (let i = 0, l = from.length; i < l; i++) {
            str = str.replace(new RegExp(from.charAt(i), 'g'), to.charAt(i));
        }
        str = str
            .replace(/[^a-z0-9 -]/g, '') // remove invalid chars
            .replace(/\s+/g, '-') // collapse whitespace and replace by -
            .replace(/-+/g, '-'); // collapse dashes

        return str;
    }

    update(i) {
        if (!this.state.overlayShowed) {
            this.setState({ overlayShowed: i[0] });
        }
    }

    render() {
        const name = _(this.props.overlay.name);
        const scientificName = _(this.props.overlay.scientificName);
        const unit = _(this.props.version.referenceUnit);
        const source = _(this.props.version.source);
        const symptoms = _(this.props.version.symptoms);
        const managementRecommendations = _(this.props.version.managementRecommendations);
        const items = this.props.version.mapValues;
        const confirmedResistance = this.props.version.confirmedResistance;
        const metrics = this.props.version.mapMetrics || [];

        const metrics_by_group = metrics.reduce((storage, metric) => {
            const group = metric['group'];
            storage[group] = storage[group] || [];
            storage?.[group]?.push(metric);
            return storage;
        }, {});

        const textColorChoices = {
            low: 'green',
            medium: 'yellow',
            high: 'red',
        };

        const PerformanceImpactChoices = {
            low: _('bajo'),
            medium: _('medio'),
            high: _('alto'),
        };

        const WeedMetricsChoices = {
            low: _('baja probabilidad'),
            medium: _('media probabilidad'),
            high: _('alta probabilidad'),
        };

        const hibernationAndMigrationChoices = {
            low: _('no'),
            medium: _('parcial'),
            high: _('sí'),
        };

        const controlStrategiesChoices = {
            low: _('Controla'),
            medium: _('Controla parcialmente'),
            high: _('No controla'),
        };

        const controlMetricChoices = {
            low: _('Control efectivo'),
            medium: _('Control medianamente efectivo'),
            high: _('Control no efectivo'),
        };

        const getGroupMetrics = (label) => {
            if (!Object.keys(metrics_by_group).length) return undefined;
            if (!Object.keys(metrics_by_group).includes(label)) return undefined;
            if (!metrics_by_group?.[label]?.length) return undefined;
            return metrics_by_group?.[label]?.sort((a, b) => {
                return a.order - b.order;
            });
        };

        const probabilityOfOccurrenceMetrics = getGroupMetrics('probability_of_occurrence');
        const performanceImpactMetrics = getGroupMetrics('performance_impact');
        const hibernationAndMigrationMetrics = getGroupMetrics('hibernation_and_migration');
        const controlStrategiesMetrics = getGroupMetrics('control_strategies');
        const resistanceMetrics = getGroupMetrics('resistance');
        const fallowManagementMetrics = getGroupMetrics('fallow_management');
        const chemicalControlStrategiesMetrics = getGroupMetrics('chemical_control_strategies');

        const images =
            this.props.version.images &&
            this.props.version.images
                .slice(0, 3)
                .map((image) => <img key={image.id} src={image.file} alt={scientificName} className={styles.image} />);

        const itemsToShow = [
            {
                name: 'Referencias',
                enable: this.props.overlay.type !== 'humidity',
                id: 'references',
            },
            {
                name: 'Referencias',
                enable: this.props.overlay.type === 'humidity',
                id: 'humidityReferences',
            },
            {
                name: 'Probabilidad de Ocurrencia',
                enable: probabilityOfOccurrenceMetrics && !!probabilityOfOccurrenceMetrics.length,
                id: 'probability',
            },
            {
                name: 'Galería de Fotos',
                enable: images && !!images.length,
                id: 'images',
            },
            {
                name: 'Impacto en el rendimiento',
                enable: performanceImpactMetrics && !!performanceImpactMetrics.length,
                id: 'performanceImpact',
            },
            {
                name: 'Resistencia',
                enable: resistanceMetrics && !!resistanceMetrics.length,
                id: 'resistence',
            },
            {
                name: 'Manejo en Barbecho',
                enable: fallowManagementMetrics && !!fallowManagementMetrics.length,
                id: 'fallow',
            },
            {
                name: 'Estrategias de control',
                enable: controlStrategiesMetrics && !!controlStrategiesMetrics.length,
                id: 'control',
            },
            {
                name: 'Estrategias de control químico',
                enable: chemicalControlStrategiesMetrics && !!chemicalControlStrategiesMetrics.length,
                id: 'chemicalControl',
            },
            {
                name: 'Recomendaciones',
                enable: managementRecommendations,
                id: 'recommendations',
            },
        ].filter((item) => {
            return item.enable;
        });

        this.update(itemsToShow);

        return (
            <div style={{ width: '100%' }}>
                <Grid container direction='column' sx={{ p: 3, maxWidth: '100vw' }}>
                    <Grid item sx={{ pb: 1 }}>
                        <Title size='sm' bolder>
                            {name}
                        </Title>
                    </Grid>
                    <Grid>
                        {scientificName && (
                            <Title italic size='sm' sx={{ pb: 1 }}>
                                {scientificName}
                            </Title>
                        )}
                    </Grid>
                    <Grid item>
                        {symptoms && (
                            <Title size='sm'>
                                <ShowMoreText
                                    lines={5}
                                    more={_('Ver más')}
                                    less={_('Ver menos')}
                                    anchorClass={styles.showMore}
                                    expanded={false}
                                >
                                    {symptoms}
                                </ShowMoreText>
                            </Title>
                        )}
                    </Grid>
                </Grid>

                {itemsToShow.length > 1 && this.props.expanded && (
                    <Grid
                        sx={{
                            py: 2,
                            borderBottom: '1px solid #e8e8e8',
                            borderTop: '1px solid #e8e8e8',
                            width: '100vw',
                        }}
                    >
                        <ScrollableChips
                            onClick={(v) => this.setState({ overlayShowed: v })}
                            values={itemsToShow}
                            selected={this.state.overlayShowed}
                        />
                    </Grid>
                )}
                {this.props.expanded && (
                    <Fragment>
                        {this.state.overlayShowed && this.state.overlayShowed.id === 'control' && (
                            <Grid container direction='column' sx={{ p: 0 }}>
                                <Grid sx={{ p: 2 }}>
                                    {this.state.overlayShowed && (
                                        <Title className={styles.title}>{this.state.overlayShowed.name}</Title>
                                    )}
                                </Grid>

                                {controlStrategiesMetrics &&
                                    controlStrategiesMetrics.map((metric) => (
                                        <Grid
                                            container
                                            alignItems='center'
                                            justifyContent='space-between'
                                            sx={{ p: 2, border: '1px solid #e8e8e8' }}
                                        >
                                            <Title className={styles.title}>{_(metric.name)}</Title>

                                            <Title
                                                key={metric.id}
                                                className={classNames(
                                                    styles.impactValue,
                                                    styles.value,
                                                    styles.title,
                                                    styles.background,
                                                    styles.control,
                                                    styles[textColorChoices[metric.value]],
                                                    styles[`${textColorChoices[metric.value]}-background`]
                                                )}
                                            >
                                                {metric.value ? controlStrategiesChoices[metric.value] : '-'}
                                            </Title>
                                        </Grid>
                                    ))}
                            </Grid>
                        )}

                        {this.state.overlayShowed && this.state.overlayShowed.id === 'recommendations' && (
                            <Grid sx={{ p: 2 }}>
                                <Grid sx={{ pb: 2 }}>
                                    {this.state.overlayShowed && (
                                        <Title className={styles.title}>{this.state.overlayShowed.name}</Title>
                                    )}
                                </Grid>
                                <Title size='sm'>{managementRecommendations}</Title>
                            </Grid>
                        )}

                        {this.state.overlayShowed && this.state.overlayShowed.id === 'fallow' && (
                            <Grid>
                                <Grid container direction='column' sx={{ p: 0 }}>
                                    {fallowManagementMetrics &&
                                        fallowManagementMetrics.map((metric) => (
                                            <Grid
                                                container
                                                alignItems='center'
                                                justifyContent='space-between'
                                                sx={{ p: 2, border: '1px solid #e8e8e8' }}
                                            >
                                                <Title className={styles.title}>{_(metric.name)}</Title>

                                                <Title
                                                    key={metric.id}
                                                    className={classNames(
                                                        styles.impactValue,
                                                        styles.value,
                                                        styles.title,
                                                        styles.background,
                                                        styles.control,
                                                        styles[textColorChoices[metric.value]],
                                                        styles[`${textColorChoices[metric.value]}-background`]
                                                    )}
                                                >
                                                    {metric.value ? controlMetricChoices[metric.value] : '-'}
                                                </Title>
                                            </Grid>
                                        ))}
                                </Grid>
                            </Grid>
                        )}

                        {this.state.overlayShowed && this.state.overlayShowed.id === 'performanceImpact' && (
                            <Grid container sx={{ p: 0 }} direction='column'>
                                <Grid
                                    container
                                    alignItems='center'
                                    justifyContent='space-between'
                                    sx={{ p: 2, border: '1px solid #e8e8e8' }}
                                >
                                    <Title className={styles.title}>{this.state.overlayShowed.name}</Title>

                                    {performanceImpactMetrics &&
                                        performanceImpactMetrics.map((metric) => (
                                            <Title
                                                key={metric.id}
                                                className={classNames(
                                                    styles.impactValue,
                                                    styles.value,
                                                    styles.title,
                                                    styles.background,
                                                    styles[textColorChoices[metric.value]],
                                                    styles[`${textColorChoices[metric.value]}-background`]
                                                )}
                                            >
                                                {PerformanceImpactChoices[metric.value]}
                                            </Title>
                                        ))}
                                </Grid>

                                <Grid item direction='column' sx={{ p: 0 }}>
                                    {hibernationAndMigrationMetrics &&
                                        hibernationAndMigrationMetrics.map((metric) => {
                                            return (
                                                <Grid
                                                    container
                                                    alignItems='center'
                                                    justifyContent='space-between'
                                                    sx={{ p: 2, border: '1px solid #e8e8e8' }}
                                                >
                                                    <Title className={styles.title}> {_(metric.name)}</Title>
                                                    <Title
                                                        className={classNames(
                                                            styles.value,
                                                            styles.title,
                                                            styles[metric.value],
                                                            styles.background,
                                                            styles[`${metric.value}-background`]
                                                        )}
                                                    >
                                                        {hibernationAndMigrationChoices[metric.value]}
                                                    </Title>
                                                </Grid>
                                            );
                                        })}
                                </Grid>
                            </Grid>
                        )}

                        {this.state.overlayShowed && this.state.overlayShowed.id === 'chemicalControl' && (
                            <Grid>
                                <Grid sx={{ p: 2 }}>
                                    {this.state.overlayShowed && (
                                        <Title className={styles.title}>{this.state.overlayShowed.name}</Title>
                                    )}
                                </Grid>
                                <Grid container direction='column' sx={{ p: 0 }}>
                                    {chemicalControlStrategiesMetrics &&
                                        chemicalControlStrategiesMetrics.map((metric) => (
                                            <Grid
                                                container
                                                alignItems='center'
                                                justifyContent='space-between'
                                                sx={{ p: 2, border: '1px solid #e8e8e8' }}
                                            >
                                                <Title className={styles.title}>{_(metric.name)}</Title>

                                                <Title
                                                    key={metric.id}
                                                    className={classNames(
                                                        styles.impactValue,
                                                        styles.value,
                                                        styles.title,
                                                        styles.background,
                                                        styles.control,
                                                        styles[textColorChoices[metric.value]],
                                                        styles[`${textColorChoices[metric.value]}-background`]
                                                    )}
                                                >
                                                    {metric.value ? controlMetricChoices[metric.value] : '-'}
                                                </Title>
                                            </Grid>
                                        ))}
                                </Grid>
                            </Grid>
                        )}
                        <Grid sx={{ py: 2 }}>
                            {this.state.overlayShowed && this.state.overlayShowed.id === 'images' && (
                                <Fragment>
                                    <Grid sx={{ pl: 2 }}>
                                        {this.state.overlayShowed && (
                                            <Title className={styles.title}>{this.state.overlayShowed.name}</Title>
                                        )}
                                    </Grid>

                                    <div className={styles.imageContainer}>{images}</div>
                                </Fragment>
                            )}

                            {this.state.overlayShowed && this.state.overlayShowed.id === 'references' && (
                                <Grid container direction='column' sx={{ px: 2 }} spacing={2}>
                                    <Grid item>{this.renderVersionSelect(this.props.overlay.versions)}</Grid>
                                    <Grid item>
                                        <NewReferenceItems
                                            items={items}
                                            spodoptera={this.props.overlay.type === 'spodoptera_forecast'}
                                        />
                                    </Grid>
                                    <Grid container sx={{ gap: '5px', p: 2 }}>
                                        <Grid item>
                                            {unit && (
                                                <Title size='sm'>
                                                    <span> {`${_('Unidad:')} `}</span>
                                                    <ReactMarkdown className={styles.unitMd}>{unit}</ReactMarkdown>
                                                </Title>
                                            )}
                                        </Grid>
                                        <Grid item> {unit && source && <Title size='sm'>-</Title>}</Grid>
                                        <Grid item>
                                            {source && <Title size='sm'>{`${_('Fuente:')} ${source}`}</Title>}
                                        </Grid>
                                    </Grid>
                                </Grid>
                            )}

                            {this.state.overlayShowed && this.state.overlayShowed.id === 'probability' && (
                                <Grid sx={{ px: 2 }}>
                                    <Grid sx={{ pb: 2 }}>
                                        {this.state.overlayShowed && (
                                            <Title className={styles.title}>{this.state.overlayShowed.name}</Title>
                                        )}
                                    </Grid>
                                    <div className={styles.probabilities}>
                                        {probabilityOfOccurrenceMetrics &&
                                            probabilityOfOccurrenceMetrics.map((metric) => (
                                                <div key={metric.id} className={styles.probability}>
                                                    <div>{_(metric.name)}</div>
                                                    <div
                                                        className={classNames(
                                                            styles.probabilityValue,
                                                            styles[textColorChoices[metric.value]]
                                                        )}
                                                    >
                                                        {PerformanceImpactChoices[metric.value]}
                                                    </div>
                                                    <div
                                                        className={classNames(
                                                            styles.svg,
                                                            styles[
                                                                this.string_to_slug(`${metric.name.es}-${metric.value}`)
                                                            ],
                                                            styles[
                                                                this.string_to_slug(`${metric.name.en}-${metric.value}`)
                                                            ],
                                                            styles[
                                                                this.string_to_slug(`${metric.name.pt}-${metric.value}`)
                                                            ]
                                                        )}
                                                    />
                                                </div>
                                            ))}
                                    </div>
                                </Grid>
                            )}

                            {this.state.overlayShowed && this.state.overlayShowed.id === 'humidityReferences' && (
                                <Grid container direction='column' sx={{ px: 2 }}>
                                    <Grid container direction='column'>
                                        <Grid item sx={{ py: 1 }}>
                                            <Title size='sm'>{_('Fecha')}</Title>
                                        </Grid>
                                        <Grid item sx={{ py: 1 }}>
                                            {this.renderVersionSelect(this.props.overlay.versions)}
                                        </Grid>
                                    </Grid>
                                    <Grid container direction='column'>
                                        <Grid item sx={{ py: 1 }}>
                                            <Title size='sm'>{_('Referencias')}</Title>
                                        </Grid>
                                        <Grid item sx={{ py: 1 }}>
                                            <NewReferenceItems items={items} square />
                                        </Grid>
                                    </Grid>
                                    <Grid container sx={{ gap: '5px', py: 1 }}>
                                        <Grid item>{unit && <Title size='sm'>{`${_('Unidad:')} ${unit}`}</Title>}</Grid>
                                        <Grid item> {unit && source && <Title size='sm'>-</Title>}</Grid>
                                        <Grid item>
                                            {source && <Title size='sm'>{`${_('Fuente:')} ${source}`}</Title>}
                                        </Grid>
                                    </Grid>
                                </Grid>
                            )}
                        </Grid>
                    </Fragment>
                )}

                {/*
                 

                //TODO: VER (ID resistence) PORQ NINGUNO TIENE ESTO 
                        {resistanceMetrics.map((metric) => (
                            <div
                                key={metric.id}
                                className={classNames(styles.impactValue, styles[textColorChoices[metric.value]])}
                            >
                                {_(PerformanceImpactChoices[metric.value])}
                            </div>
                        ))}
                        {confirmedResistance && (
                            <div className={styles.managementRecommendation}>{confirmedResistance}</div>
                        )}
                )}
                */}
            </div>
        );
    }
}
